import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { apiService } from "src/services/api.service";
import { getDataRows } from "src/utils/ScoresGrid/getDataRows";
import Icon from "src/components/Icon/Icon";

import styles from "./MyResults.module.scss";

interface IResultRow {
    scoreId: string;
    callName: string;
    eventTitle?: string;
    showType: string;
    sanctioningClub?: string;
    showName: string;
    showDate: string;
    eventHandle?: string;
    eventId?: string;
    element: string;
    level: string;
    status?: string;
    result: string;
    // Detailed stats (populated by getDataRows / getShowSpecificData)
    score?: number | string;
    time?: number | string;
    searchTime?: number | string;
    finalTime?: number | string;
    placement?: string;
    faults?: number | string;
    faultCount?: number | string;
    totalDeductions?: number | string;
    feet?: number | string;
    inches?: number | string;
    highestWeight?: number | string;
    percentPulled?: number | string;
}

// Container elements whose level is the whole story ("Veteran", not
// "Non-Licensed Classes · Veteran").
const NON_LICENSED_ELEMENTS: string[] = [
    Constants.dog_class_element.conformation_non_licensed,
    Constants.dog_class_element.performance_non_licensed_obedience,
];

const MyResults: React.FC = () => {
    const [results, setResults] = useState<IResultRow[]>([]);

    useEffect(() => {
        apiService.getOwnerScores({ $limit: 3 }).then((scores: IScore[]) => {
            const rows = getDataRows(scores) as IResultRow[];
            setResults(rows);
        }).catch(() => {
            // silently fail — sidebar is non-critical
        });
    }, []);

    if (results.length === 0) return null;

    const hasResult = (result?: string): boolean =>
        !!result && result.toLowerCase() !== "no result";

    const getResultClass = (result: string): string => {
        if (!hasResult(result)) return styles.resultNeutral;
        const r = result.toLowerCase();
        if (r === "qualified" || r === "passed" || r === "pass" || r === "complete") return styles.resultPass;
        if (r === "nq" || r === "failed" || r === "fail" || r === "disqualified") return styles.resultFail;
        return styles.resultNeutral;
    };

    // Short form for the result tile ("Q", "NQ", "DQ", "–" when nothing yet).
    const tileLabel = (result: string): string => {
        if (!hasResult(result)) return "–";
        const r = result.toLowerCase();
        if (r === "qualified") return "Q";
        if (r === "disqualified") return "DQ";
        if (r === "passed") return "Pass";
        if (r === "failed") return "Fail";
        if (result.length <= 4) return result.toUpperCase();
        return result.split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 3);
    };

    const buildStats = (row: IResultRow): { label: string; value: string }[] => {
        const stats: { label: string; value: string }[] = [];

        if (row.score !== undefined && row.score !== "" && row.score !== null) {
            stats.push({ label: "Score", value: String(row.score) });
        }
        if (row.time !== undefined && row.time !== "" && row.time !== null && row.time !== "N/A") {
            stats.push({ label: "Time", value: String(row.time) });
        } else if (row.searchTime !== undefined && row.searchTime !== "" && row.searchTime !== null) {
            stats.push({ label: "Time", value: String(row.searchTime) });
        } else if (row.finalTime !== undefined && row.finalTime !== "" && row.finalTime !== null) {
            stats.push({ label: "Time", value: String(row.finalTime) });
        }
        if (row.placement !== undefined && row.placement !== "" && row.placement !== null && row.placement !== "N/A") {
            stats.push({ label: "Place", value: String(row.placement) });
        }
        if (row.faults !== undefined && row.faults !== "" && row.faults !== null) {
            stats.push({ label: "Faults", value: String(row.faults) });
        } else if (row.faultCount !== undefined && row.faultCount !== "" && row.faultCount !== null) {
            stats.push({ label: "Faults", value: String(row.faultCount) });
        }
        if (row.totalDeductions !== undefined && row.totalDeductions !== "" && row.totalDeductions !== null) {
            stats.push({ label: "Deductions", value: String(row.totalDeductions) });
        }
        if (row.feet !== undefined && row.feet !== "" && row.feet !== null) {
            const dist = row.inches !== undefined && row.inches !== "" ? `${row.feet}' ${row.inches}"` : `${row.feet}'`;
            stats.push({ label: "Distance", value: dist });
        }
        if (row.highestWeight !== undefined && row.highestWeight !== "" && row.highestWeight !== null) {
            stats.push({ label: "Weight", value: String(row.highestWeight) });
        }
        if (row.percentPulled !== undefined && row.percentPulled !== "" && row.percentPulled !== null) {
            stats.push({ label: "% Pulled", value: String(row.percentPulled) });
        }

        return stats;
    };

    return (
        <div className={styles.MyResults}>
            <div className={styles.header}>
                <div className={styles.title}>Recent results</div>
            </div>
            <ul className={styles.list}>
                {results.map((row) => {
                    const stats = buildStats(row);
                    const pending = !!row.status && row.status.toLowerCase() === "pending" && !hasResult(row.result);
                    const eventPath = row.eventHandle || row.eventId ? `/e/${row.eventHandle ?? row.eventId}` : null;
                    const title = row.eventTitle || row.showName || "—";
                    const date = moment(row.showDate, "MM/DD/YYYY");
                    const meta = row.callName;
                    const statsLine = [
                        pending ? "Pending" : null,
                        ...stats.map((s) => `${s.label} ${s.value}`),
                    ].filter(Boolean).join(" · ");
                    const classLine = [
                        NON_LICENSED_ELEMENTS.includes(row.element) ? null : row.element,
                        row.level !== row.element ? row.level : null,
                    ].filter(Boolean).join(" · ");
                    const label = tileLabel(row.result);

                    return (
                        <li key={row.scoreId} className={styles.item}>
                            <div
                                className={`${styles.resultTile} ${getResultClass(row.result)} ${label.length > 2 ? styles.resultTileLong : ""}`}
                                title={hasResult(row.result) ? row.result : "No result yet"}
                            >
                                {label}
                            </div>

                            <div className={styles.body}>
                                <div className={styles.topRow}>
                                    <span className={styles.typeLabel}>
                                        {[row.sanctioningClub, row.showType].filter(Boolean).join(" · ")}
                                    </span>
                                    {date.isValid() && <span className={styles.date}>{date.format("MMM D")}</span>}
                                </div>
                                {eventPath ? (
                                    <Link to={eventPath} className={styles.eventName} title={title}>{title}</Link>
                                ) : (
                                    <span className={styles.eventName} title={title}>{title}</span>
                                )}
                                {meta && <span className={styles.meta} title={meta}>{meta}</span>}
                                {classLine && <span className={styles.classLine} title={classLine}>{classLine}</span>}
                                {statsLine && <span className={styles.statsLine} title={statsLine}>{statsLine}</span>}
                            </div>
                        </li>
                    );
                })}
            </ul>
            <Link to="/account?t=2" className={styles.viewAll}>
                <span>View all results</span>
                <Icon type="caretRight" className={styles.viewAllIcon} />
            </Link>
        </div>
    );
};

export default MyResults;
